import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CheckList, FAQAccordion, CTABand, Rise, Eyebrow } from "@/components/chrome";
import { SITE } from "@/data/site";
import { INSIGHTS } from "@/data/insights";
import { findProvince, findCity, findService, paths } from "@/lib/seo";
import { serviceCityContent, hashCode } from "@/lib/content";
import NotFound from "@/pages/NotFound";

export default function ServiceCityPage() {
  const { provinceSlug, citySlug, serviceSlug } = useParams();
  const province = findProvince(provinceSlug);
  const city = province && findCity(provinceSlug, citySlug);
  const service = findService(serviceSlug);
  if (!province || !city || !service) return <NotFound />;

  const c = serviceCityContent(service, city, province);
  const related = INSIGHTS.filter((_, i) => (i + hashCode(service.slug)) % 3 === 0).slice(0, 2);
  const short = service.short.toLowerCase();

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Locations", path: "/locations" },
    { name: province.name, path: paths.province(province.slug) },
    { name: city.name, path: paths.city(province.slug, city.slug) },
    { name: service.name },
  ];

  return (
    <div data-testid="service-city-page">
      <PageHero prefix="service-city" crumbs={crumbs}
        eyebrow={`${service.category} — ${city.name}, ${province.code}`}
        title={c.h1} lead={service.tagline} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-city-intro">
        <div className="max-w-3xl space-y-5">
          {c.intro.map((p, i) => <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>)}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="service-city-included">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>The checklist</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What's included in {short} in {city.name}
            </h2></Rise>
          <div className="mt-8"><CheckList items={c.includes} testid="service-city-checklist" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-city-process">
        <Rise><Eyebrow>How it works</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Our {short} process in {city.name}
          </h2></Rise>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {service.process.map((step, i) => (
            <Rise key={step.t} delay={i * 0.06} className="border-t border-slate-200 pt-5">
              <p className="font-mono text-sm font-semibold text-cyan-700">0{i + 1}</p>
              <h3 className="mt-2 font-display text-base font-semibold text-slate-900">{step.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.d}</p>
            </Rise>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="service-city-local">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Local coverage</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Serving {city.name} and nearby {province.name} communities
            </h2></Rise>
          <div className="mt-6 max-w-3xl space-y-5">
            {c.local.map((p, i) => <p key={i} className="leading-relaxed text-slate-600">{p}</p>)}
          </div>
          {c.nearby.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm" data-testid="nearby-service-links">
              {c.nearby.map((n) => (
                <Link key={n.slug} to={paths.serviceCity(province.slug, n.slug, service.slug)}
                  data-testid={`nearby-${service.slug}-${n.slug}`}
                  className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                  {service.short} in {n.name} <ArrowRight className="h-3 w-3 text-slate-300" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-city-pricing">
        <Rise><Eyebrow>Pricing</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {service.short} pricing in {city.name}
          </h2></Rise>
        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <div className="space-y-5">
            {c.pricing.map((p, i) => <p key={i} className="leading-relaxed text-slate-600">{p}</p>)}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">What shapes your quote</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              {service.priceFactors.map((f) => (
                <li key={f} className="flex items-start gap-3 border-t border-slate-100 pt-3">{f}</li>
              ))}
            </ul>
            <a href={SITE.phoneHref} data-testid="pricing-call-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700">
              Call {SITE.phone} for a flat-rate quote
            </a>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="service-city-other-services">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>More in {city.name}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Other cleaning services in {city.name}
            </h2></Rise>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm" data-testid="other-service-links">
            {c.others.map((s) => (
              <Link key={s.slug} to={paths.serviceCity(province.slug, city.slug, s.slug)}
                data-testid={`other-service-${s.slug}`}
                className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                {s.short} in {city.name} <ArrowRight className="h-3 w-3 text-slate-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-city-faq-section">
        <Rise><Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {service.name} in {city.name} — FAQs
          </h2></Rise>
        <div className="mt-8 max-w-3xl"><FAQAccordion faqs={c.faqs} testid="service-city-faq" /></div>
      </section>

      {related.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 pb-4" data-testid="service-city-insights">
          <Rise><Eyebrow>From our insights</Eyebrow></Rise>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {related.map((post) => (
              <Link key={post.slug} to={paths.insight(post.slug)} data-testid={`related-insight-${post.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-700">{post.category} · {post.readMinutes} min read</p>
                <h3 className="mt-3 font-display text-lg font-semibold leading-snug tracking-tight text-slate-900">{post.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTABand title={`Book ${short} in ${city.name} today`}
        lead={`No forms, no waiting. Call ${SITE.phone} or email ${SITE.email} — most ${city.name} quotes arrive within one business hour.`}
        prefix="service-city-cta" />
    </div>
  );
}
